'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { nanoid } from 'nanoid';
import { Switch } from '@mantine/core';

import { Tags } from '@/elements/tags/tags.component';
import {
  Container,
  Search,
  IconWrap,
  Input,
  ActiveFilters,
  FilterBadge,
  MenuWrap,
  Menu,
} from './search-bar.elements';

import { useDebounce } from '@/hooks/use-debounce.hook';

import { IconSearch, IconCircleX, IconX, IconPlus } from '@tabler/icons-react';

const SearchBar = ({
  hasExpiredRegistrations,
  search,
  setSearch,
  filters,
  setFilters,
  tags,
  onTagClick,
  activeTag,
  target,
}) => {
  const [menuItems, setMenuItems] = useState([
    {
      id: 'durationLong',
      label: 'Sastoji se od više modula (trajanje > 3h)',
      name: 'Trajanje',
      disabled: false,
    },
    {
      id: 'durationShort',
      label: 'Sastoji se od jednog modula (trajanje < 3h)',
      name: 'Trajanje',
      disabled: false,
    },
    { id: 'live', label: 'Predavanja su uživo', name: 'Uživo', disabled: false },
    { id: 'online', label: 'Predavanja su online', name: 'Online', disabled: false },
    { id: 'recording', label: 'Dostupna je snimka predavanja', name: 'Snimka', disabled: false },
    {
      id: 'upcoming',
      label: 'Sljedeći susret održava se uskoro',
      name: 'Nadolazeće',
      disabled: false,
    },
  ]);
  const [showMenu, setShowMenu] = useState(false);
  const [localSearch, setLocalSearch] = useState(search);
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const debouncedSetSearch = useDebounce((value) => {
    setSearch(value);
  }, 300);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);
    debouncedSetSearch(value);
  };

  const handleSearchClick = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const addFilter = useCallback(
    (e) => {
      e.preventDefault();
      if (localSearch.trim().length > 0) {
        setFilters((prev) => [...prev, { id: nanoid(), value: localSearch, type: 'search' }]);
        setLocalSearch('');
        setSearch('');
      }
    },
    [localSearch, setFilters, setSearch]
  );

  const toggleFilter = useCallback(
    (e) => {
      const id = e.target.id;
      const name = e.target.name;

      setFilters((prev) => {
        if (prev.some((f) => f.type === id)) {
          return prev.filter((f) => f.type !== id);
        }
        return [...prev, { id: nanoid(), value: name, type: id }];
      });
    },
    [setFilters]
  );

  const clearSearch = useCallback(() => {
    setLocalSearch('');
    setSearch('');
  }, [setSearch]);

  const sortedFilters = useMemo(
    () => filters.sort((a, b) => (b.type === 'search') - (a.type === 'search')),
    [filters]
  );

  const placeholderText = useMemo(() => {
    if (target === 'educations') return 'Pretraži naše edukacije, treninge i webinare';
    if (target === 'my-profile') return 'Pretraži svoje edukacije';
    if (target === 'lecturers') return 'Pretraži naše predavače';
    return 'Pretraži naše objave';
  }, [target]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    if (
      target === 'my-profile' &&
      hasExpiredRegistrations &&
      !filters.some((f) => f.id === 'showExpired')
    ) {
      setMenuItems((prev) => {
        // Prevent duplicate entries
        if (prev.some((item) => item.id === 'showExpired')) return prev;
        return [
          ...prev,
          { id: 'showExpired', label: 'Prikaži moje istekle edukacije', name: 'Istekle edukacije' },
        ];
      });
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [target, hasExpiredRegistrations, filters]);

  useEffect(() => {
    if (filters.find((f) => f.type === 'durationLong')) {
      setMenuItems((prev) =>
        prev.map((f) => (f.id === 'durationShort' ? { ...f, disabled: true } : f))
      );
    } else {
      setMenuItems((prev) =>
        prev.map((f) => (f.id === 'durationShort' ? { ...f, disabled: false } : f))
      );
    }

    if (filters.find((f) => f.type === 'durationShort')) {
      setMenuItems((prev) =>
        prev.map((f) => (f.id === 'durationLong' ? { ...f, disabled: true } : f))
      );
    } else {
      setMenuItems((prev) =>
        prev.map((f) => (f.id === 'durationLong' ? { ...f, disabled: false } : f))
      );
    }
  }, [filters]);

  return (
    <Container>
      <ActiveFilters>
        {['educations', 'my-profile', 'lecturers'].includes(target) && (
          <MenuWrap ref={menuRef}>
            <FilterBadge $addFilter onClick={() => setShowMenu(!showMenu)}>
              <IconWrap style={{ padding: 8 }}>
                <IconPlus size={16} />
              </IconWrap>
              <span>Dodaj filter</span>
            </FilterBadge>
            {showMenu && (
              <Menu>
                {menuItems.map(({ id, label, name, disabled }) => (
                  <div key={id}>
                    <Switch
                      id={id}
                      name={name}
                      checked={filters.some((f) => f.type === id)}
                      onChange={toggleFilter}
                      label={label}
                      withThumbIndicator={false}
                      disabled={disabled}
                    />
                  </div>
                ))}
              </Menu>
            )}
          </MenuWrap>
        )}
        {sortedFilters.map(({ id, value, type }) => (
          <FilterBadge key={id}>
            <span>
              {type === 'search'
                ? value
                : type === 'showExpired'
                  ? 'Istekle edukacije'
                  : type === 'durationLong'
                    ? `${value}: > 3h`
                    : type === 'durationShort'
                      ? `${value}: < 3h`
                      : ['live', 'online', 'recording'].includes(type)
                        ? `Sadrži: ${value}`
                        : 'Nadolazeći susret'}
            </span>
            <IconWrap
              style={{ padding: 8 }}
              $bordered
              onClick={() => setFilters((prev) => prev.filter((term) => term.id !== id))}
            >
              <IconX size={16} />
            </IconWrap>
          </FilterBadge>
        ))}
      </ActiveFilters>
      <Search onSubmit={addFilter} onClick={handleSearchClick}>
        <IconWrap>
          <IconSearch size={20} />
        </IconWrap>
        <Input
          ref={inputRef}
          placeholder={placeholderText}
          value={localSearch}
          onChange={handleSearchChange}
        />
        {localSearch.length > 0 && (
          <IconWrap>
            <IconCircleX size={24} onClick={clearSearch} />
          </IconWrap>
        )}
      </Search>
      {tags && <Tags tags={tags} onTagClick={onTagClick} activeTag={activeTag} />}
    </Container>
  );
};

export { SearchBar };
